(function () {
  'use strict';

  const MAX_AUDIO_BYTES = 25 * 1024 * 1024;

  function autoResizeTextarea(instance, textarea) {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }

  function syncAdaptiveTextControls(instance) {
    const textarea = instance.querySelector('[data-adaptive-textarea]');
    const submitBtn = instance.querySelector('[data-adaptive-text-submit]');
    if (!submitBtn) return;
    submitBtn.disabled = !(textarea && textarea.value.trim());
  }

  function renderAdaptiveSuggestions(instance, suggestions) {
    const suggestionsWrap = instance.querySelector('[data-adaptive-suggestions]');
    const chipsEl = instance.querySelector('[data-adaptive-suggestion-chips]');

    if (!suggestionsWrap || !chipsEl) return;

    const normalizedSuggestions = Array.from(new Set(
      (Array.isArray(suggestions) ? suggestions : [])
        .map((value) => (value || '').toString().trim())
        .filter(Boolean)
    ));

    chipsEl.innerHTML = '';
    suggestionsWrap.hidden = normalizedSuggestions.length === 0;

    if (!normalizedSuggestions.length) return;

    chipsEl.innerHTML = normalizedSuggestions.map((suggestion) =>
      `<button class="anchor-chip" type="button" data-adaptive-suggestion-chip data-suggestion-value="${instance._escapeAttr(suggestion)}">${instance._escapeHtml(suggestion)}</button>`
    ).join('');
  }

  function submitAnchorOptionOnly(instance, value, label) {
    if (instance._inflight) return;
    instance._resetAssessmentState?.();
    instance._ensureSessionId();

    const turn = instance._serializeConversationTurn(
      'primary_concern',
      "What's your primary hair or scalp concern?",
      value,
      label,
      'option'
    );
    instance.conversationHistory = [turn];
    instance.questionCount = 1;
    instance._setConversationHistory(instance.conversationHistory);

    instance._track('ConsultationStarted', {
      content_name: 'Tara Hair Consultation',
      primary_concern: label
    });
    instance._track('ConsultationProgress', {
      content_name: 'Tara Hair Consultation',
      question_number: 1,
      answer: label
    });

    instance._pendingBridgeText = null;
    instance._prefetchNextQuestion();
    setTimeout(() => instance._advanceFromPrefetch(), 150);
  }

  function initIntake(instance) {
    const intakeText = instance.querySelector('[data-intake-text]');
    const sendBtn = instance.querySelector('[data-intake-submit]');
    instance._resetAnchorVoiceNudge();

    if (intakeText) {
      intakeText.addEventListener('input', () => {
        if (intakeText.value.trim()) clearTimeout(instance._anchorAutoAdvance);
        autoResizeTextarea(instance, intakeText);
        if (sendBtn) sendBtn.disabled = !intakeText.value.trim();
        if (intakeText.value.trim()) instance._dismissAnchorVoiceNudge();
        if (instance._anchorSelectedLabel && intakeText.value.trim() !== instance._anchorSelectedLabel) {
          instance.querySelectorAll('[data-chip]').forEach((chip) => chip.classList.remove('anchor-chip--selected'));
          instance._anchorSelectedValue = null;
          instance._anchorSelectedLabel = null;
        }
      });

      intakeText.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          submitAnchorIntake(instance);
        }
      });
    }

    const adaptiveTextarea = instance.querySelector('[data-adaptive-textarea]');
    if (adaptiveTextarea) {
      adaptiveTextarea.addEventListener('input', () => {
        autoResizeTextarea(instance, adaptiveTextarea);
        const selectedSuggestion = adaptiveTextarea.dataset.selectedSuggestion || '';
        if (selectedSuggestion && adaptiveTextarea.value.trim() !== selectedSuggestion) {
          instance.querySelectorAll('[data-adaptive-suggestion-chip]').forEach((chip) =>
            chip.classList.remove('anchor-chip--selected'));
          delete adaptiveTextarea.dataset.selectedSuggestion;
        }
        syncAdaptiveTextControls(instance);
      });

      adaptiveTextarea.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          submitAdaptiveText(instance);
        }
      });
    }
  }

  function submitAnchorIntake(instance) {
    if (instance._inflight) return;
    clearTimeout(instance._anchorAutoAdvance);

    const intakeText = instance.querySelector('[data-intake-text]');
    const freeText = intakeText ? intakeText.value.trim() : '';
    if (!freeText && !instance._anchorSelectedValue) return;

    if (!freeText && instance._anchorSelectedValue) {
      submitAnchorOptionOnly(instance, instance._anchorSelectedValue, instance._anchorSelectedLabel);
      return;
    }

    instance._resetAssessmentState?.();
    instance._ensureSessionId();

    const turn = {
      question_id: 'primary_concern',
      question: "What's your primary hair or scalp concern?",
      answer: freeText,
      answer_label: freeText,
      input_type: 'free_text'
    };

    if (instance._anchorSelectedValue) {
      turn.selected_value = instance._anchorSelectedValue;
      turn.selected_label = instance._anchorSelectedLabel;
    }

    instance.conversationHistory = [turn];
    instance.questionCount = 1;
    instance._setConversationHistory(instance.conversationHistory);

    instance._track('ConsultationStarted', {
      content_name: 'Tara Hair Consultation',
      primary_concern: freeText
    });
    instance._track('ConsultationProgress', {
      content_name: 'Tara Hair Consultation',
      question_number: 1,
      answer: freeText,
      input_type: 'free_text'
    });

    if (intakeText) intakeText.value = '';
    instance._anchorSelectedValue = null;
    instance._anchorSelectedLabel = null;

    instance._pendingBridgeText = null;
    instance._prefetchNextQuestion();
    setTimeout(() => instance._advanceFromPrefetch(), 150);
  }

  function submitAdaptiveText(instance) {
    if (instance._inflight) return;
    const textarea = instance.querySelector('[data-adaptive-textarea]');
    if (!textarea || !textarea.value.trim()) return;

    instance._resetAssessmentState?.();
    const freeText = textarea.value.trim();
    const questionText = instance.querySelector('[data-adaptive-question]')?.textContent || '';
    const questionId = instance._lastAdaptiveResponse?.question_id || 'unknown';

    const turn = {
      question_id: questionId,
      question: questionText,
      answer: freeText,
      answer_label: freeText,
      input_type: 'free_text'
    };

    instance.conversationHistory.push(turn);
    instance.questionCount++;
    instance._setConversationHistory(instance.conversationHistory);

    instance._track('ConsultationProgress', {
      content_name: 'Tara Hair Consultation',
      question_number: instance.questionCount,
      answer: freeText,
      input_type: 'free_text'
    });

    textarea.value = '';
    delete textarea.dataset.selectedSuggestion;

    if (instance._lastQuestionComplete) {
      instance._enterContactCapture(null);
    } else {
      instance._fetchNextQuestion();
    }
  }

  async function toggleVoiceRecording(instance, micBtn) {
    if (instance._isRecording) {
      stopVoiceRecording(instance);
      return;
    }

    clearTimeout(instance._anchorAutoAdvance);

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.warn('[Tara Consultation] Mic access denied:', error);
      showIntakeHint(instance, micBtn, 'mic_denied', true);
      return;
    }

    instance._isRecording = true;
    instance._audioChunks = [];
    instance._activeMicBtn = micBtn;
    micBtn.classList.add('anchor-prompt__mic--recording');

    const sendBtn = micBtn.closest('.consultation-step')?.querySelector('[data-intake-submit], [data-adaptive-text-submit]');
    if (sendBtn) sendBtn.disabled = false;

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');

    instance._mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    instance._mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) instance._audioChunks.push(event.data);
    };
    instance._mediaRecorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      micBtn.classList.remove('anchor-prompt__mic--recording');
      instance._isRecording = false;

      if (instance._audioChunks.length) {
        micBtn.classList.add('mic--uploading');
        const blob = new Blob(instance._audioChunks, {
          type: instance._mediaRecorder.mimeType || 'audio/webm'
        });
        submitVoiceNote(instance, blob, micBtn);
      } else {
        const textarea = micBtn.closest('.consultation-step')?.querySelector('[data-intake-text], [data-adaptive-textarea]');
        if (sendBtn) sendBtn.disabled = !(textarea && textarea.value.trim());
      }
    };

    instance._mediaRecorder.start();
  }

  function stopVoiceRecording(instance) {
    if (instance._mediaRecorder && instance._isRecording) {
      try {
        instance._mediaRecorder.stop();
      } catch (error) {
        /* already stopped */
      }
    }
    instance._isRecording = false;
    instance.querySelectorAll('.anchor-prompt__mic--recording').forEach((btn) =>
      btn.classList.remove('anchor-prompt__mic--recording'));
  }

  async function submitVoiceNote(instance, blob, micBtn) {
    if (blob && blob.size >= MAX_AUDIO_BYTES) {
      showIntakeHint(instance, micBtn, 'audio_too_large', true);
      const sendBtn = micBtn.closest('.consultation-step')?.querySelector('[data-intake-submit], [data-adaptive-text-submit]');
      const textarea = micBtn.closest('.consultation-step')?.querySelector('[data-intake-text], [data-adaptive-textarea]');
      if (sendBtn) sendBtn.disabled = !(textarea && textarea.value.trim());
      return;
    }

    const voiceUrl = getVoiceUploadUrl(instance);
    if (!voiceUrl) {
      console.warn('[Tara Consultation] worker_url not configured - cannot send voice note');
      showIntakeHint(instance, micBtn, 'upload_failed', true);
      return;
    }

    if (instance._inflight) {
      instance.abortController?.abort();
      instance._inflight = false;
      instance._setInflightUI(false);
    }

    showIntakeHint(instance, micBtn, 'sending', false);
    console.log('[Tara Consultation] Voice upload ->', voiceUrl);

    instance._ensureSessionId();
    instance._resetAssessmentState?.();
    clearTimeout(instance._anchorAutoAdvance);

    const isAnchor = instance.currentStep === 'anchor';
    const originStep = instance.currentStep;
    const questionId = isAnchor ? 'primary_concern' : (instance._lastAdaptiveResponse?.question_id || 'unknown');
    const questionText = isAnchor
      ? "What's your primary hair or scalp concern?"
      : (instance.querySelector('[data-adaptive-question]')?.textContent || '');

    const turn = {
      question_id: questionId,
      question: questionText,
      answer: '',
      answer_label: '',
      input_type: 'voice_note'
    };

    if (isAnchor && instance._anchorSelectedValue) {
      turn.selected_value = instance._anchorSelectedValue;
      turn.selected_label = instance._anchorSelectedLabel;
    }

    if (isAnchor) {
      instance.conversationHistory = [turn];
      instance.questionCount = 1;
    } else {
      instance.conversationHistory.push(turn);
      instance.questionCount++;
    }
    instance._setConversationHistory(instance.conversationHistory);

    if (isAnchor) {
      instance._track('ConsultationStarted', {
        content_name: 'Tara Hair Consultation',
        primary_concern: '(voice note)',
        input_type: 'voice_note'
      });
    }
    instance._track('ConsultationProgress', {
      content_name: 'Tara Hair Consultation',
      question_number: instance.questionCount,
      input_type: 'voice_note'
    });

    const ext = blob.type.includes('webm') ? 'webm' : 'mp4';
    const locale = instance._getUiLocale();
    const workerLocale = instance._normalizeWorkerLocale(locale);
    const storePayload = instance._buildStorePayload();
    const formData = new FormData();
    formData.append('file', blob, `voice.${ext}`);
    formData.append('session_id', instance._ensureSessionId());
    formData.append('question_id', questionId);
    formData.append('question', questionText);
    formData.append('provider', instance.config.modelProvider || 'openai');
    formData.append('conversation_history', JSON.stringify(instance.conversationHistory));
    if (instance.config.debug) formData.append('debug', 'true');
    if (isAnchor && instance._anchorSelectedValue) {
      formData.append('selected_value', instance._anchorSelectedValue);
      formData.append('selected_label', instance._anchorSelectedLabel || '');
    }
    if (workerLocale) {
      formData.append('ui_locale', workerLocale);
      formData.append('response_language', workerLocale);
      formData.append('locale', workerLocale);
      formData.append('language', workerLocale);
    }
    if (storePayload.market) formData.append('market', storePayload.market);
    if (storePayload.key || storePayload.market) formData.append('store', JSON.stringify(storePayload));

    const reqId = ++instance._requestId;
    instance._inflight = true;
    instance._setInflightUI(true);
    instance.abortController?.abort();
    instance.abortController = new AbortController();
    const timeout = setTimeout(() => instance.abortController.abort(), 60000);
    instance._showBridgeText();
    instance._showStep('question-loading');
    instance._log('voice-upload-start', {
      reqId,
      url: voiceUrl,
      blobSize: blob.size,
      blobType: blob.type
    });

    try {
      if (!window.TaraConsultationApi || typeof window.TaraConsultationApi.submitFormData !== 'function') {
        throw new Error('Consultation API helper unavailable');
      }

      const request = window.TaraConsultationApi.submitFormData({
        baseUrl: instance.config.workerUrl,
        path: '/api/question',
        body: formData,
        signal: instance.abortController.signal,
        fallbackMessage: 'Voice upload request failed'
      });

      const { response, data } = await request;
      clearTimeout(timeout);

      instance._log('voice-upload-response', { reqId, status: response.status });

      if (reqId !== instance._requestId) {
        instance._log('voice-response-stale', { reqId, current: instance._requestId });
        return;
      }

      if (data.session_id) {
        instance.sessionId = data.session_id;
        instance._setStoredSessionId(data.session_id);
      }
      if (typeof instance._persistProgressState === 'function') {
        instance._persistProgressState(data.question_id || '');
      }

      const transcript = data.transcript_text || data._voice_transcript;
      if (transcript) {
        const lastTurn = instance.conversationHistory[instance.conversationHistory.length - 1];
        if (lastTurn && lastTurn.input_type === 'voice_note') {
          lastTurn.answer = transcript;
          lastTurn.answer_label = transcript;
        }
        instance._setConversationHistory(instance.conversationHistory);
      }

      instance._lastAdaptiveResponse = data;
      instance._lastQuestionComplete = data.complete || false;
      hideIntakeHint(instance, micBtn);
      instance._log('voice-response-ok', {
        reqId,
        questionId: data.question_id,
        complete: data.complete,
        hasTranscript: !!(data.transcript_text || data._voice_transcript),
        transcriptLang: data.transcript_language || null,
        inputType: data.source_input_type || data.input_type || null
      });

      if (data.needs_voice_retry) {
        instance._log('voice-retry', {
          reason: data.voice_retry_reason,
          questionId: data.question_id
        });
        instance._renderVoiceRetry(data);
        instance._showStep('adaptive');
      } else if (data.complete) {
        instance._enterContactCapture(data.wait_experience || null);
      } else {
        instance._renderAdaptiveQuestion(data);
        instance._showStep('adaptive');
      }
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        if (reqId !== instance._requestId) return;
        console.warn('[Tara Consultation] Voice upload timed out');
        showIntakeHint(instance, micBtn, 'upload_failed', true);
        instance._showStep(originStep, 'back');
        return;
      }
      if (reqId !== instance._requestId) return;
      console.error('[Tara Consultation] Voice upload failed:', error);
      showIntakeHint(instance, micBtn, 'upload_failed', true);
      instance._showStep(originStep, 'back');
    } finally {
      micBtn.classList.remove('mic--uploading');
      if (reqId === instance._requestId) {
        instance._inflight = false;
        instance._setInflightUI(false);
      }
    }
  }

  function getVoiceUploadUrl(instance) {
    const base = instance.config.workerUrl;
    if (!base) return null;
    if (window.TaraConsultationApi && typeof window.TaraConsultationApi.resolveUrl === 'function') {
      return window.TaraConsultationApi.resolveUrl('/api/question', base);
    }
    return `${base.replace(/\/+$/, '')}/api/question`;
  }

  function showIntakeHint(instance, micBtn, key, isError) {
    const t = instance.config?.i18n || {};
    const hints = {
      sending: t.sendingVoice || 'Sending voice note...',
      mic_denied: t.micDenied || 'Microphone access was denied. You can type your answer instead.',
      upload_failed: t.uploadFailed || 'Voice upload failed. Please type your answer instead.',
      audio_too_large: t.errorAudioTooLarge || 'Your voice note was too large (max 25 MB). Please re-record.'
    };
    const container = micBtn.closest('.anchor-prompt') || micBtn.closest('.consultation-intake') || micBtn.closest('.consultation-adaptive-text');
    const hint = container?.querySelector('[data-intake-hint], [data-adaptive-hint]');
    if (!hint) return;
    hint.textContent = hints[key] || '';
    hint.className = `consultation-intake__hint${isError ? ' consultation-intake__hint--error' : ' consultation-intake__hint--transcribing'}`;
    hint.hidden = false;
  }

  function hideIntakeHint(instance, micBtn) {
    const container = micBtn.closest('.anchor-prompt') || micBtn.closest('.consultation-intake') || micBtn.closest('.consultation-adaptive-text');
    const hint = container?.querySelector('[data-intake-hint], [data-adaptive-hint]');
    if (hint) hint.hidden = true;
  }

  window.TaraConsultationInput = Object.freeze({
    autoResizeTextarea,
    syncAdaptiveTextControls,
    renderAdaptiveSuggestions,
    submitAnchorOptionOnly,
    initIntake,
    submitAnchorIntake,
    submitAdaptiveText,
    toggleVoiceRecording,
    stopVoiceRecording,
    submitVoiceNote,
    getVoiceUploadUrl,
    showIntakeHint,
    hideIntakeHint,
  });
})();
